// CustomerDetailsModal.tsx
import React, { useEffect, useState, useCallback } from "react";
import styles from "./ClienteEditModal.module.css";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import { documentHelper } from "@/utils/common/documentHelper";
import { phoneNumberHelper } from "@/utils/common/phoneNumberHelper";
import { useCustomerSubscriptions } from "../hooks/useCustomerSubscriptions";
import { useCustomerAssociates } from "../hooks/useCustomerAssociates";
import { Copy, Plus, X, Edit, Trash2 } from "lucide-react";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import SubscriptionModal, { SubscriptionItem } from "../../Inscricoes/SubscriptionsModal";
import CreateSubscriptionModal from "../../Inscricoes/CreateSubscriptionModal";
import AssociateModal from "./AssociateModal";

interface AffiliateOption {
  id: string;
  name: string | null;
  email: string;
  legalRepresentativeName?: string | null;
}

type Customer = {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  companyDocument?: string;
  postalBoxNumber?: string;
  legalRepresentativeName?: string;
  legalRepresentativeDocument?: string;
  birthDate?: string | null;
  address?: string | null;
  comment?: string | null;
  createdAt?: string;
  updatedAt?: string;
  coworkingId?: string;
  affiliateId?: string | null;
  affiliate?: any;
};

type Props = {
  isOpen: boolean;
  customer: Customer;
  onClose: () => void;
  onSaved?: (updated: Customer) => void;
};

const CustomerDetailsModal: React.FC<Props> = ({ isOpen, customer, onClose, onSaved }) => {
  const userContext = useUser();
  const token = userContext?.userData?.auth;
  const subsData = useCustomerSubscriptions(customer.id);
  const { loading: subsLoading, subscriptions: subsRaw, error: subsError } = subsData;
  const refetchSubscriptions = (subsData as any)?.refetch || (() => {});
  const { loading: associatesLoading, associates, error: associatesError, refetch: refetchAssociates } = useCustomerAssociates(customer.id);

  const subscriptions = Array.isArray(subsRaw) ? subsRaw : (subsRaw?.items ?? []);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState(customer.name ?? "");
  const [email, setEmail] = useState(customer.email ?? "");
  const [phone, setPhone] = useState(phoneNumberHelper.formatPhone(customer.phone ?? ""));
  const [companyDocument, setCompanyDocument] = useState(documentHelper.formatCNPJ(customer.companyDocument ?? ""));
  const [postalBoxNumber, setPostalBoxNumber] = useState(customer.postalBoxNumber ?? "");
  const [legalRepresentativeName, setLegalRepresentativeName] = useState(customer.legalRepresentativeName ?? "");
  const [legalRepresentativeDocument, setLegalRepresentativeDocument] = useState(documentHelper.formatCPF(customer.legalRepresentativeDocument ?? ""));
  const [birthDate, setBirthDate] = useState(customer.birthDate ?? "");
  const [address, setAddress] = useState(customer.address ?? "");
  const [comment, setComment] = useState(customer.comment ?? "");
  const modal = useGlobalModal();

  // Affiliate selection state
  const [affiliateOptions, setAffiliateOptions] = useState<AffiliateOption[]>([]);
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState<AffiliateOption | null>(null);
  const [affiliateInputValue, setAffiliateInputValue] = useState("");

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [createSubscriptionOpen, setCreateSubscriptionOpen] = useState(false);
  const [subscriptionDetailsOpen, setSubscriptionDetailsOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionItem | null>(null);
  const [associateModalOpen, setAssociateModalOpen] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState<any | null>(null);

  // Search affiliates function
  const searchAffiliates = useCallback(async (query: string) => {
    if (!token) return;
    
    setAffiliateLoading(true);
    try {
      const resp = await api.get('/customer/search-affiliates', {
        params: { query, limit: 20 },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
      // Filter out the current customer from affiliate options
      const list = (Array.isArray(raw) ? raw : []).filter((c: AffiliateOption) => c.id !== customer.id);
      setAffiliateOptions(list);
    } catch (err: any) {
      console.error('Erro ao buscar afiliados:', err);
      setAffiliateOptions([]);
    } finally {
      setAffiliateLoading(false);
    }
  }, [token, customer.id]);

  // Debounce affiliate search
  useEffect(() => {
    if (!editing) return;
    
    const timeoutId = setTimeout(() => {
      if (affiliateInputValue.length >= 1) {
        searchAffiliates(affiliateInputValue);
      } else {
        searchAffiliates("");
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [affiliateInputValue, editing, searchAffiliates]);

  useEffect(() => {
    setName(customer.name ?? "");
    setEmail(customer.email ?? "");
    setPhone(phoneNumberHelper.formatPhone(customer.phone ?? ""));
    setCompanyDocument(documentHelper.formatCNPJ(customer.companyDocument ?? ""));
    setPostalBoxNumber(customer.postalBoxNumber ?? "");
    setLegalRepresentativeName(customer.legalRepresentativeName ?? "");
    setLegalRepresentativeDocument(documentHelper.formatCPF(customer.legalRepresentativeDocument ?? ""));
    setBirthDate(customer.birthDate ?? "");
    setAddress(customer.address ?? "");
    setComment(customer.comment ?? "");
    setEditing(false);
    
    // Set initial affiliate if customer has one
    if (customer.affiliate) {
      setSelectedAffiliate({
        id: customer.affiliate.id,
        name: customer.affiliate.name,
        email: customer.affiliate.email || "",
        legalRepresentativeName: customer.affiliate.legalRepresentativeName,
      });
      setAffiliateInputValue(customer.affiliate.name || customer.affiliate.email || "");
    } else {
      setSelectedAffiliate(null);
      setAffiliateInputValue("");
    }
  }, [customer]);


  if (!isOpen) return null;

  function handleEdit(){
    setEditing(true);
    // Load initial affiliate options when entering edit mode
    searchAffiliates("");
  }

  async function copyId(id?: string) {
  if (!id) return;
  try {
    if (typeof window !== "undefined" && window.navigator.clipboard?.writeText) {
      await window.navigator.clipboard.writeText(id);
    } else {
      const ta = document.createElement("textarea");
      ta.value = id;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedId(id);
    window.setTimeout(() => setCopiedId(prev => (prev === id ? null : prev)), 2000);
  } catch (err) {
    console.error("Erro ao copiar id:", err);
  }
}

  // Guard extra: só salva se estivermos em modo edição
  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault?.();
    if (!editing) return; // <- proteção extra
    if (!token) {
      modal.show({
        type: "error",
        title: "Sessão expirada",
        message: "Token ausente. Faça login novamente.",
        autoClose: 3500,
      });
      return;
    }
    setSaving(true);
    try {
      const body: any = {
        name: name?.trim() || undefined,
        email: email?.trim() || undefined,
        phone: phone ? phoneNumberHelper.cleanPhone(phone) : undefined,
        companyDocument: companyDocument ? documentHelper.cleanDocument(companyDocument) : undefined,
        postalBoxNumber: postalBoxNumber?.trim() || undefined,
        legalRepresentativeName: legalRepresentativeName?.trim() || undefined,
        legalRepresentativeDocument: legalRepresentativeDocument ? documentHelper.cleanDocument(legalRepresentativeDocument) : undefined,
        birthDate: birthDate?.trim() || undefined,
        address: address?.trim() || undefined,
        comment: comment.trim().length > 0 ? comment.trim() : null,
        affiliateId: selectedAffiliate?.id || null,
      };
      Object.keys(body).forEach(k => (body[k] === undefined) && delete body[k]);

      const resp: any = await api.patch(`/customer/${customer.id}`, body, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = (resp && (resp as any).data) ? (resp as any).data : resp;
      const updatedCustomer: Customer = data?.id ? data : { ...customer, ...body, updatedAt: new Date().toISOString() };

      const successMessage = "Cliente atualizado com sucesso!";
      const id = modal.show({ type: "success", title: "Sucesso", message: successMessage, autoClose: 3000 });
      // atualiza UI imediatamente
      setEditing(false);
      onSaved && onSaved(updatedCustomer);

      // delay para fechar/modal hide/navegar
      window.setTimeout(() => {
        try { modal.hide(id); } catch(e) {}
        onClose && onClose();
        window.location.reload();
      }, 2500);

      //onClose();

    } catch (err: any) {
      console.error("Erro ao atualizar cliente:", err);
      const message = err?.response?.data?.message ?? err?.message ?? "Erro ao salvar cliente";
      modal.show({
        type: "error",
        title: "Erro ao salvar",
        message,
        autoClose: 4500,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // reset aos valores originais do prop e sair do modo edição
    setName(customer.name ?? "");
    setEmail(customer.email ?? "");
    setPhone(phoneNumberHelper.formatPhone(customer.phone ?? ""));
    setCompanyDocument(documentHelper.formatCNPJ(customer.companyDocument ?? ""));
    setPostalBoxNumber(customer.postalBoxNumber ?? "");
    setLegalRepresentativeName(customer.legalRepresentativeName ?? "");
    setLegalRepresentativeDocument(documentHelper.formatCPF(customer.legalRepresentativeDocument ?? ""));
    setBirthDate(customer.birthDate ?? "");
    setAddress(customer.address ?? "");
    setComment(customer.comment ?? "");
    setEditing(false);
    
    // Reset affiliate to original
    if (customer.affiliate) {
      setSelectedAffiliate({
        id: customer.affiliate.id,
        name: customer.affiliate.name,
        email: customer.affiliate.email || "",
        legalRepresentativeName: customer.affiliate.legalRepresentativeName,
      });
      setAffiliateInputValue(customer.affiliate.name || customer.affiliate.email || "");
    } else {
      setSelectedAffiliate(null);
      setAffiliateInputValue("");
    }
  };

  const openSubscriptionDetails = (subscription: any) => {
    const normalized: SubscriptionItem = {
      ...subscription,
      customer: subscription?.customer ?? {
        id: customer.id,
        name: customer.name,
        email: customer.email,
      },
    };
    setSelectedSubscription(normalized);
    setSubscriptionDetailsOpen(true);
  };

  const handleOpenAssociateModal = (associate: any | null = null) => {
    setSelectedAssociate(associate);
    setAssociateModalOpen(true);
  };

  const handleCloseAssociateModal = () => {
    setAssociateModalOpen(false);
    setSelectedAssociate(null);
    refetchAssociates();
  };

  const handleDeleteAssociate = async (associateId: string) => {
    if (!token) return;
    const result = modal.show({
      type: 'confirm' as any,
      title: 'Confirmar Exclusão',
      message: 'Tem certeza que deseja excluir este associado? Esta ação não pode ser desfeita.',
    });
    
    if (result) {
      const deletionModalId = modal.show({ type: 'loading' as any, title: 'Excluindo...', message: 'Aguarde enquanto o associado é excluído.' });
      try {
        await api.delete(`/customer/${customer.id}/associates/${associateId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        modal.hide(deletionModalId);
        modal.show({ type: 'success' as any, title: 'Sucesso', message: 'Associado excluído com sucesso.', autoClose: 3000 });
        refetchAssociates();
      } catch (err: any) {
        console.error("Erro ao excluir associado:", err);
        modal.hide(deletionModalId);
        const message = err?.response?.data?.message ?? err?.message ?? "Erro ao excluir associado";
        modal.show({ type: 'error' as any, title: 'Erro', message, autoClose: 4500 });
      }
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalFrame}>
        <div className={styles.modalContent} role="dialog" aria-modal="true" aria-label="Detalhes do Cliente">
          <div className={styles.flexSeparator} style={{ marginBottom: 10 }}>
            <h2 style={{ marginTop: 0, marginBottom: 0 }}>Detalhes do Cliente</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {!editing && (
                <button
                  type="button"
                  onClick={handleEdit}
                  className={styles.primaryButton}
                >
                  Editar
                </button>
              )}
              <button className={styles.closeButton} onClick={onClose} aria-label="Fechar modal">
                <X size={18} />
              </button>
            </div>
          </div>

          <form className={styles.clienteExpandAreaForm} onSubmit={handleSave} onKeyDown={(e) => {
            // evita submit por Enter quando não estamos editando
            if (!editing && e.key === 'Enter') {
              e.preventDefault();
            }
          }}
    >
            <div className={styles.formFieldsContainer}>
              <label>
                Empresa
                <input className={styles.inputField} value={name} onChange={e => setName(e.target.value)} disabled={!editing} />
              </label>

              <label>
                Email
                <input className={styles.inputField} value={email} onChange={e => setEmail(e.target.value)} disabled={!editing} />
              </label>

              <label>
                Telefone
                <input className={styles.inputField} value={phone} onChange={e => setPhone(phoneNumberHelper.formatPhone(e.target.value.replace(/\D/g, "")))} disabled={!editing} />
              </label>

              <label>
                CNPJ da Empresa
                <input className={styles.inputField} value={companyDocument} onChange={e => setCompanyDocument(documentHelper.formatCNPJ(e.target.value.replace(/\D/g, "")))} disabled={!editing} />
              </label>

              <label>
                Caixa Postal
                <input className={styles.inputField} value={postalBoxNumber} onChange={e => setPostalBoxNumber(e.target.value)} disabled={!editing} />
              </label>

              <label>
                Representante Legal
                <input className={styles.inputField} value={legalRepresentativeName} onChange={e => setLegalRepresentativeName(e.target.value)} disabled={!editing} />
              </label>

              <label>
                CPF do Representante Legal
                <input className={styles.inputField} value={legalRepresentativeDocument} onChange={e => setLegalRepresentativeDocument(documentHelper.formatCPF(e.target.value.replace(/\D/g, "")))} disabled={!editing} />
              </label>

              <label>
                Data de Nascimento
                <input className={styles.inputField} type="date" value={birthDate ? birthDate.split('T')[0] : ""} onChange={e => setBirthDate(e.target.value)} disabled={!editing} />
              </label>

              <label>
                Endereço
                <input className={styles.inputField} value={address} onChange={e => setAddress(e.target.value)} disabled={!editing} />
              </label>

              <label>
                Afiliado
                {editing ? (
                  <Autocomplete
                    options={affiliateOptions}
                    getOptionLabel={(option) => 
                      option.legalRepresentativeName 
                        ? `${option.legalRepresentativeName} (${option.email})` 
                        : option.name 
                          ? `${option.name} (${option.email})` 
                          : option.email
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedAffiliate}
                    onChange={(_, newValue) => setSelectedAffiliate(newValue)}
                    inputValue={affiliateInputValue}
                    onInputChange={(_, newInputValue) => setAffiliateInputValue(newInputValue)}
                    loading={affiliateLoading}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 500 }}>{option.legalRepresentativeName || option.name || 'Sem nome'}</span>
                          <span style={{ fontSize: '0.85em', color: '#666' }}>{option.email}</span>
                        </div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Buscar por nome ou email..."
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {affiliateLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText="Nenhum cliente encontrado"
                    loadingText="Buscando..."
                    sx={{ 
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                ) : (
                  <input 
                    className={styles.inputField} 
                    value={
                      customer.affiliate 
                        ? `${customer.affiliate.legalRepresentativeName || customer.affiliate.name || ''} (${customer.affiliate.email || customer.affiliateId || ''})`.trim()
                        : "Nenhum afiliado"
                    } 
                    disabled 
                  />
                )}
              </label>

              <label>
                ID do Cliente
                <input className={styles.inputField} value={customer.id} disabled />
              </label>

              <label>
                Coworking ID
                <input className={styles.inputField} value={customer.coworkingId ?? ""} disabled />
              </label>

              <label>
                Criado em
                <input className={styles.inputField} value={customer.createdAt ? new Date(customer.createdAt).toLocaleString() : ""} disabled />
              </label>

              <label>
                Atualizado em
                <input className={styles.inputField} value={customer.updatedAt ? new Date(customer.updatedAt).toLocaleString() : ""} disabled />
              </label>
            </div>

            <div className={styles.clienteExpandAreaButtons} style={{ marginTop: 12 }}>
              {!editing ? (
                <>
                  <button type="button" className={styles.newSubscriptionButton} onClick={() => setCreateSubscriptionOpen(true)}>
                    <Plus size={14} />
                    <span>Nova Inscrição</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Save is submit */}
                  <button type="submit" disabled={saving} style={{ padding: "8px 16px", background: "#7e22ce", color: "#fff", border: "none", borderRadius: 4 }}>
                    {saving ? "Salvando..." : "Salvar"}
                  </button>
                  <button type="button" onClick={handleCancelEdit} style={{ padding: "8px 16px", background: "#f3f3f3", color: "#7e22ce", border: "none", borderRadius: 4 }}>
                    Cancelar
                  </button>
                </>
              )}
            </div>
          </form>

          {!editing && (
            <>
              <div style={{ marginTop: 20 }}>
                <h3>Inscrições</h3>
                {subsLoading ? (
                  <div style={{ padding: 12 }}>Carregando inscrições...</div>
                ) : subsError ? (
                  <div style={{ color: "crimson", padding: 12 }}>{String(subsError)}</div>
                ) : (!subscriptions || subscriptions.length === 0) ? (
                  <div style={{ padding: 12 }}>Nenhuma inscrição encontrada para este cliente.</div>
                ) : (
                  <div style={{ overflowX: "auto", marginTop: 8 }}>
                    <table className={styles.table}>
                      <thead>
                        <tr className={styles.tableHeaderRow}>
                          <th style={{ padding: 8, textAlign: "left" }}>ID</th>
                          <th style={{ padding: 8, textAlign: "left" }}>Produto</th>
                          <th style={{ padding: 8, textAlign: "left" }}>Início</th>
                          <th style={{ padding: 8, textAlign: "left" }}>Fim</th>
                          <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                          <th style={{ padding: 8, textAlign: "left" }}>Preço</th>
                          <th style={{ padding: 8, textAlign: "left" }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptions.map((s: any) => (
                          <tr key={s.id}>
                          <td style={{ padding: 8 }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <button
                                type="button"
                                className={styles.copyBtn}
                                aria-label={`Copiar código ${s.id}`}
                                onClick={() => copyId(s.id)}
                                title="Copiar código"
                              >
                                <Copy size={14} />
                              </button>
                              {copiedId === s.id && <span className={styles.copiedBadge}>Copiado</span>}
                            </div>
                          </td>
                          <td style={{ padding: 8 }}>{s.product?.name ?? s.productId ?? "—"}</td>
                          <td style={{ padding: 8 }}>{s.startDate ? new Date(s.startDate).toLocaleDateString() : "—"}</td>
                          <td style={{ padding: 8 }}>{s.endDate ? new Date(s.endDate).toLocaleDateString() : "—"}</td>
                          <td style={{ padding: 8 }}>{s.status ?? "—"}</td>
                          <td style={{ padding: 8 }}>{s.product?.price ?? "—"}</td>
                          <td style={{ padding: 8 }}>
                            <button type="button" className={styles.secondaryButton} onClick={() => openSubscriptionDetails(s)}>
                              +Detalhes
                            </button>
                          </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 20 }}>
                <div className={styles.flexSeparator} style={{ marginBottom: 10 }}>
                  <h3>Associados</h3>
                  <button type="button" className={styles.newSubscriptionButton} onClick={() => handleOpenAssociateModal()}>
                    <Plus size={14} />
                    <span>Novo Associado</span>
                  </button>
                </div>
                {associatesLoading ? (
                  <div style={{ padding: 12 }}>Carregando associados...</div>
                ) : associatesError ? (
                  <div style={{ color: "crimson", padding: 12 }}>{String(associatesError)}</div>
                ) : (!associates || associates.length === 0) ? (
                  <div style={{ padding: 12 }}>Nenhum associado encontrado para este cliente.</div>
                ) : (
                  <div style={{ overflowX: "auto", marginTop: 8 }}>
                    <table className={styles.table}>
                      <thead>
                        <tr className={styles.tableHeaderRow}>
                          <th style={{ padding: 8, textAlign: "left" }}>Nome</th>
                          <th style={{ padding: 8, textAlign: "left" }}>Email</th>
                          <th style={{ padding: 8, textAlign: "left" }}>Telefone</th>
                          <th style={{ padding: 8, textAlign: "left" }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {associates.map((a: any) => (
                          <tr key={a.id}>
                          <td style={{ padding: 8 }}>{a.name ?? "—"}</td>
                          <td style={{ padding: 8 }}>{a.email ?? "—"}</td>
                          <td style={{ padding: 8 }}>{a.phone ? phoneNumberHelper.formatPhone(a.phone) : "—"}</td>
                          <td style={{ padding: 8, display: 'flex', gap: '8px' }}>
                            <button type="button" className={styles.secondaryButton} onClick={() => handleOpenAssociateModal(a)}>
                              <Edit size={14} />
                            </button>
                            <button type="button" className={`${styles.secondaryButton} ${styles.deleteButton}`} onClick={() => handleDeleteAssociate(a.id)}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          <CreateSubscriptionModal
            open={createSubscriptionOpen}
            onClose={() => setCreateSubscriptionOpen(false)}
            onCreated={() => {
              setCreateSubscriptionOpen(false);
              refetchSubscriptions();
            }}
            customer={customer}
          />

          <SubscriptionModal
            open={subscriptionDetailsOpen}
            onClose={() => setSubscriptionDetailsOpen(false)}
            subscription={selectedSubscription}
            onSaved={(updated) => {
              setSelectedSubscription(updated);
              refetchSubscriptions();
            }}
          />

          {associateModalOpen && (
            <AssociateModal
              open={associateModalOpen}
              onClose={handleCloseAssociateModal}
              onSave={() => {}}
              associate={selectedAssociate}
              customerId={customer.id}
            />
          )}
        </div>

        <aside className={styles.commentDockOutside}>
          <div className={styles.commentPanel}>
            <label className={styles.commentLabel}>
              Comentários
              <textarea
                className={styles.commentTextArea}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                readOnly={!editing}
                title={editing ? "Clique em Salvar para confirmar alterações" : "Clique para editar"}
                onFocus={(e) => {
                  if (!editing) {
                    handleEdit();
                    window.requestAnimationFrame(() => e.currentTarget.focus());
                  }
                }}
                disabled={saving}
                placeholder="Adicione observações importantes sobre este cliente..."
              />
            </label>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CustomerDetailsModal;
